import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import {
    SealCheck, IdentificationCard, Clock, CurrencyInr, TrendUp,
    Funnel, Users, ArrowsCounterClockwise, ShieldCheck, XCircle,
    FileArrowDown, ChartLine,
} from "@phosphor-icons/react";

/**
 * SuperAdminVerificationAnalytics — platform-wide tracking of the
 * two-tier verification system:
 *   1. Paid Verified Badge (₹299 + admin approval)
 *   2. AI-KYC-verified (free, Aadhaar / PAN OCR)
 *
 * V1.5.8.16 — superadmin only. Revenue flows to NaukriDaddy, not agencies,
 * so no per-workspace scoping is exposed here.
 */

const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

const STATUS_COLORS = {
    approved: "bg-emerald-100 text-emerald-800",
    paid: "bg-cobalt/10 text-cobalt",
    pending_review: "bg-amber-100 text-amber-800",
    rejected: "bg-red-100 text-red-800",
    created: "bg-slate-100 text-slate-700",
};

function KpiTile({ icon: Icon, label, value, sub, tone = "cobalt", testid }) {
    const bg = {
        cobalt: "bg-cobalt/5 border-cobalt/20 text-cobalt",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
        amber: "bg-amber-50 border-amber-200 text-amber-800",
        splash: "bg-splash/10 border-splash/30 text-ink",
    }[tone];
    return (
        <div
            data-testid={testid}
            className={`border ${bg} p-4 sm:p-5 relative overflow-hidden`}
        >
            <div className="absolute -right-4 -top-4 opacity-10">
                <Icon size={64} weight="fill" />
            </div>
            <div className="relative">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80">
                    {label}
                </p>
                <p className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">
                    {value}
                </p>
                {sub && <p className="text-[10px] mt-1 opacity-70">{sub}</p>}
            </div>
        </div>
    );
}

function FunnelBar({ label, count, max, tone }) {
    const pct = max ? Math.max(2, Math.round((count / max) * 100)) : 2;
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs font-bold text-ink">{label}</span>
                <span className="text-xs tabular-nums text-[#525252]">
                    {fmtInt(count)} <span className="text-[#A3A3A3]">·</span>{" "}
                    <span className="text-[#A3A3A3]">{max ? Math.round((count / max) * 100) : 0}%</span>
                </span>
            </div>
            <div className="h-2 bg-[#F5F5F5] overflow-hidden">
                <div
                    style={{ width: `${pct}%` }}
                    className={`h-full transition-all duration-500 ${tone}`}
                />
            </div>
        </div>
    );
}

export default function SuperAdminVerificationAnalytics() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async (silent = false) => {
        silent ? setRefreshing(true) : setLoading(true);
        try {
            const r = await api.get("/admin/verification/analytics");
            setData(r.data);
            setErr(null);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn't fetch analytics");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };
    useEffect(() => { load(); }, []);

    const csvUrl = (tier) => `${API}/admin/verification/export.csv?tier=${tier}`;

    if (loading) {
        return (
            <div className="p-10 text-sm text-[#525252]" data-testid="verification-analytics-loading">
                <ArrowsCounterClockwise size={16} weight="bold" className="animate-spin inline mr-2" />
                Loading verification analytics…
            </div>
        );
    }
    if (err) {
        return (
            <div className="p-10" data-testid="verification-analytics-error">
                <XCircle size={22} weight="fill" className="text-red-500 mb-2" />
                <p className="text-sm text-red-600">{err}</p>
            </div>
        );
    }

    const { totals, revenue_inr, kyc_upgrade_rate, funnel, recent_orders, workspace_breakdown } = data;
    const maxFunnel = Math.max(
        funnel.signup, funnel.kyc_started, funnel.kyc_verified, funnel.badge_paid, funnel.badge_approved, 1,
    );

    return (
        <div className="p-3 sm:p-8 space-y-5 sm:space-y-6 overflow-x-hidden" data-testid="verification-analytics-page">
            {/* Header */}
            <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-1 inline-flex items-center gap-1">
                        <ShieldCheck size={11} weight="fill" /> V1.5.8.16 · Verification
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink">
                        Who&apos;s verified, who paid, who upgraded.
                    </h1>
                    <p className="text-xs text-[#A3A3A3] mt-1">
                        Refreshed {new Date(data.generated_at).toLocaleString("en-IN")}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="verification-refresh-btn"
                        onClick={() => load(true)}
                        disabled={refreshing}
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#525252] border border-[#E5E5E5] px-3 py-1.5 hover:border-ink disabled:opacity-40"
                    >
                        <ArrowsCounterClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
                        {refreshing ? "Refreshing" : "Refresh"}
                    </button>
                    <Link
                        to="/superadmin"
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-cobalt hover:underline"
                    >
                        ← Superadmin
                    </Link>
                </div>
            </div>

            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiTile
                    testid="kpi-paid-verified"
                    icon={SealCheck}
                    label="Paid Verified"
                    value={fmtInt(totals.paid_verified)}
                    sub="₹299 badge · admin-approved"
                    tone="cobalt"
                />
                <KpiTile
                    testid="kpi-kyc-verified"
                    icon={IdentificationCard}
                    label="KYC-Verified (free)"
                    value={fmtInt(totals.kyc_verified)}
                    sub="AI-verified Aadhaar / PAN"
                    tone="emerald"
                />
                <KpiTile
                    testid="kpi-pending-review"
                    icon={Clock}
                    label="Pending Review"
                    value={fmtInt(totals.pending_review + totals.paid_awaiting_kyc)}
                    sub="Paid — awaiting KYC or admin"
                    tone="amber"
                />
                <KpiTile
                    testid="kpi-revenue"
                    icon={CurrencyInr}
                    label="Badge Revenue"
                    value={fmtInr(revenue_inr)}
                    sub="Approved + in-review"
                    tone="splash"
                />
            </div>

            {/* Upgrade rate gauge */}
            <div
                data-testid="upgrade-rate-card"
                className="bg-white border border-[#E5E5E5] p-5 sm:p-6"
            >
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1 inline-flex items-center gap-1">
                            <TrendUp size={11} weight="fill" /> The KPI that matters
                        </p>
                        <h3 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-ink">
                            KYC → Paid upgrade rate
                        </h3>
                        <p className="text-xs text-[#525252] mt-1 max-w-md">
                            Share of verified candidates on the paid tier.
                            <b> Target ≥ 8%.</b> Below this, tweak the upsell hero copy or the price.
                        </p>
                    </div>
                    <div className="text-right shrink-0">
                        <p
                            data-testid="upgrade-rate-value"
                            className={`font-heading text-5xl sm:text-6xl font-black tracking-tighter ${
                                kyc_upgrade_rate >= 0.08 ? "text-emerald-600"
                                    : kyc_upgrade_rate >= 0.04 ? "text-amber-600"
                                    : "text-red-600"
                            }`}
                        >
                            {fmtPct(kyc_upgrade_rate)}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-1">
                            {fmtInt(totals.paid_verified)} of{" "}
                            {fmtInt(totals.paid_verified + totals.kyc_verified)} verified
                        </p>
                    </div>
                </div>
            </div>

            {/* Funnel */}
            <div className="bg-white border border-[#E5E5E5] p-5 sm:p-6" data-testid="funnel-card">
                <div className="flex items-center gap-2 mb-4">
                    <Funnel size={16} weight="fill" className="text-cobalt" />
                    <h3 className="font-heading text-lg font-black tracking-tight text-ink">
                        Signup → Verified funnel
                    </h3>
                </div>
                <div className="space-y-3">
                    <FunnelBar label="Signup" count={funnel.signup} max={maxFunnel} tone="bg-[#D4D4D4]" />
                    <FunnelBar label="KYC uploaded" count={funnel.kyc_started} max={maxFunnel} tone="bg-cobalt/40" />
                    <FunnelBar label="KYC verified (paid + free)" count={funnel.kyc_verified} max={maxFunnel} tone="bg-emerald-500" />
                    <FunnelBar label="Badge paid" count={funnel.badge_paid} max={maxFunnel} tone="bg-cobalt" />
                    <FunnelBar label="Badge approved" count={funnel.badge_approved} max={maxFunnel} tone="bg-cobalt/80" />
                </div>
            </div>

            {/* CSV export */}
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-5" data-testid="csv-export-card">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1">
                            Roster CSV export
                        </p>
                        <h4 className="font-heading text-lg font-black text-ink">Segment for outbound campaigns</h4>
                        <p className="text-xs text-[#525252] mt-1">
                            Filter by tier and download a CSV — feed it into Resend for a nudge blast.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {[
                            { label: "All candidates", tier: "all", tone: "border-[#E5E5E5] text-[#525252]" },
                            { label: "Paid Verified", tier: "paid", tone: "border-cobalt text-cobalt" },
                            { label: "KYC-only (upsell target)", tier: "kyc", tone: "border-emerald-500 text-emerald-700" },
                            { label: "Unverified", tier: "none", tone: "border-[#E5E5E5] text-[#525252]" },
                        ].map((o) => (
                            <a
                                key={o.tier}
                                href={csvUrl(o.tier)}
                                data-testid={`csv-${o.tier}`}
                                className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-2 border hover:bg-white ${o.tone}`}
                            >
                                <FileArrowDown size={11} weight="bold" /> {o.label}
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent orders */}
            <div className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid="recent-orders-card">
                <div className="p-4 sm:p-5 border-b border-[#F5F5F5] flex items-center gap-2">
                    <Users size={16} weight="fill" className="text-cobalt" />
                    <h3 className="font-heading text-lg font-black tracking-tight text-ink">
                        Recent badge orders
                    </h3>
                </div>
                {recent_orders.length === 0 ? (
                    <p className="p-6 text-sm text-[#A3A3A3]">No orders yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-[#FAFAFA]">
                                <tr className="text-left text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                    <th className="px-4 py-2">User</th>
                                    <th className="px-4 py-2">Status</th>
                                    <th className="px-4 py-2 text-right">Amount</th>
                                    <th className="px-4 py-2">Decided by</th>
                                    <th className="px-4 py-2">Placed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent_orders.map((o) => (
                                    <tr key={o.id} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]">
                                        <td className="px-4 py-2 truncate max-w-[220px]">{o.user_email || o.user_id}</td>
                                        <td className="px-4 py-2">
                                            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${STATUS_COLORS[o.status] || "bg-slate-100 text-slate-700"}`}>
                                                {o.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums">
                                            {fmtInr((o.amount_paise || 29900) / 100)}
                                        </td>
                                        <td className="px-4 py-2 truncate max-w-[180px] text-[#525252]">
                                            {o.decided_by_email || "—"}
                                        </td>
                                        <td className="px-4 py-2 text-[#A3A3A3]">
                                            {o.created_at ? new Date(o.created_at).toLocaleDateString("en-IN") : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Workspace breakdown */}
            <div className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid="workspace-breakdown-card">
                <div className="p-4 sm:p-5 border-b border-[#F5F5F5] flex items-center gap-2">
                    <ChartLine size={16} weight="fill" className="text-cobalt" />
                    <h3 className="font-heading text-lg font-black tracking-tight text-ink">
                        Workspace breakdown
                    </h3>
                    <span className="text-[10px] text-[#A3A3A3]">Top 25 by verified candidates</span>
                </div>
                {workspace_breakdown.length === 0 ? (
                    <p className="p-6 text-sm text-[#A3A3A3]">No data yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-[#FAFAFA]">
                                <tr className="text-left text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                    <th className="px-4 py-2">Workspace</th>
                                    <th className="px-4 py-2 text-right">Total</th>
                                    <th className="px-4 py-2 text-right">Paid</th>
                                    <th className="px-4 py-2 text-right">KYC-only</th>
                                    <th className="px-4 py-2 text-right">% Verified</th>
                                </tr>
                            </thead>
                            <tbody>
                                {workspace_breakdown.map((w) => {
                                    const verified = (w.paid || 0) + (w.kyc || 0);
                                    const pct = w.total ? (verified / w.total) : 0;
                                    return (
                                        <tr key={w.workspace_id} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]">
                                            <td className="px-4 py-2 truncate max-w-[240px] font-bold text-ink">{w.name}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{fmtInt(w.total)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums text-cobalt font-bold">{fmtInt(w.paid)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{fmtInt(w.kyc)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums text-[#525252]">
                                                {fmtPct(pct)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
